import type { FC, ReactNode } from 'react'
import type { Course } from '@/types'

import { useMemo, useState } from 'react'
import { SearchX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CourseCard } from '@/components/courses/course-card'

interface CourseCatalogProps {
    courses: Array<Course>
}

interface FilterGroupProps {
    label: string
    allLabel: string
    options: Array<string>
    value: string
    onChange: (value: string) => void
}

const ALL = 'all'

const LEVEL_ORDER = ['beginner', 'intermediate', 'advanced']

const uniqueValues = (values: Array<string>): Array<string> => Array.from(new Set(values.map(value => value.trim()).filter(Boolean)))

const levelRank = (level: string): number => {
    const index = LEVEL_ORDER.indexOf(level.trim().toLowerCase())

    return index === -1 ? LEVEL_ORDER.length : index
}

const FilterGroup: FC<FilterGroupProps> = ({
    label,
    allLabel,
    options,
    value,
    onChange
}): ReactNode => {
    return (
        <div role='group' aria-label={label} className='flex flex-wrap items-center gap-2'>
            <span className='mr-1 font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase'>
                {label}
            </span>

            {[ALL, ...options].map(option => {
                const selected = value === option

                return (
                    <button
                        key={option}
                        type='button'
                        aria-pressed={selected}
                        onClick={() => onChange(option)}
                        className={cn(
                            'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                            selected ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground/80 hover:border-foreground/30'
                        )}
                    >
                        {option === ALL ? allLabel : option}
                    </button>
                )
            })}
        </div>
    )
}

export const CourseCatalog: FC<CourseCatalogProps> = ({ courses }): ReactNode => {
    const [level, setLevel] = useState(ALL)
    const [category, setCategory] = useState(ALL)

    const levels = useMemo(
        () => uniqueValues(courses.map(course => course.level)).sort((a, b) => levelRank(a) - levelRank(b)),
        [courses]
    )

    const categories = useMemo(() => uniqueValues(courses.map(course => course.category)), [courses])

    const visible = courses.filter(course => (
        (level === ALL || course.level.trim() === level) &&
        (category === ALL || course.category.trim() === category)
    ))

    const reset = (): void => {
        setLevel(ALL)
        setCategory(ALL)
    }

    return (
        <div>
            <div className='flex flex-col gap-4 rounded-3xl border bg-card p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between'>
                <div className='flex flex-col gap-4 lg:flex-row lg:gap-8'>
                    {levels.length > 1 && (
                        <FilterGroup label="Level" allLabel="All levels" options={levels} value={level} onChange={setLevel} />
                    )}

                    {categories.length > 1 && (
                        <FilterGroup label="Topic" allLabel="All topics" options={categories} value={category} onChange={setCategory} />
                    )}
                </div>

                <p className='text-sm text-muted-foreground' aria-live='polite'>
                    Showing <span className='font-semibold text-foreground'>{visible.length}</span> of {courses.length} courses
                </p>
            </div>

            {visible.length > 0
                ? (
                    <div className='mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {visible.map(course => (
                            <CourseCard key={course.id} course={course} />
                        ))}
                    </div>
                )
                : (
                    <div className='mt-8 flex flex-col items-center rounded-3xl border border-dashed bg-card px-6 py-14 text-center'>
                        <span className='flex size-12 items-center justify-center rounded-2xl bg-secondary text-primary'>
                            <SearchX className='size-6' aria-hidden='true' />
                        </span>

                        <h2 className='mt-5 text-xl font-semibold'>
                            No courses match these filters
                        </h2>

                        <p className='mt-2 max-w-md text-muted-foreground'>
                            Try another level or topic, or show every course again.
                        </p>

                        <button
                            type='button'
                            onClick={reset}
                            className='mt-6 rounded-full border border-foreground/15 bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring'
                        >
                            Show all courses
                        </button>
                    </div>
                )}
        </div>
    )
}
